import { useState, useEffect } from "react";
import { useT } from "../../contexts/LanguageContext";
import { Lightning, Robot, FilePdf, X } from "@phosphor-icons/react";

/**
 * OnboardingTour — 3-step first-time overlay shown on the dashboard after
 * a user's very first login. Uses localStorage so it never re-appears.
 */
const STORAGE_KEY = "pmcoach_onboarding_v1";

const STEPS = [
  {
    icon: Lightning,
    title_tr: "1. Hemen pratik yap",
    title_en: "1. Start practicing",
    body_tr:
      "Dashboard'un üst kısmındaki 'Pratik başlat' butonuyla 10 soruluk bir tura başla. İlk turun bile hazırlık skoruna dokunur.",
    body_en:
      "Tap 'Start practice' at the top of the dashboard to take a 10-question set. Even your first attempt updates your readiness score.",
  },
  {
    icon: Robot,
    title_tr: "2. AI Koç'a sor",
    title_en: "2. Ask the AI Coach",
    body_tr:
      "Her denemeden sonra 'Koça sor' kartıyla neden yanlış yaptığını, sırada ne çalışman gerektiğini AI koçtan öğren.",
    body_en:
      "After each attempt, use the 'Ask coach' card to understand why you missed a question and what to study next.",
  },
  {
    icon: FilePdf,
    title_tr: "3. PDF raporunu indir",
    title_en: "3. Download your PDF report",
    body_tr:
      "Hazır hissettiğinde 'PDF rapor indir' butonuyla 6 sayfalık kişisel hazırlık raporunu indirebilirsin. Sınavdan önce mutlaka bak.",
    body_en:
      "When you're ready, download your 6-page personal readiness PDF from the 'PDF report' button. Review it before exam day.",
  },
];

export default function OnboardingTour() {
  const { lang } = useT();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!localStorage.getItem(STORAGE_KEY)) {
      // Small delay so dashboard content mounts first
      const id = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(id);
    }
  }, []);

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, "done");
    setVisible(false);
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else finish();
  };

  const back = () => {
    if (step > 0) setStep(step - 1);
  };

  if (!visible) return null;

  const s = STEPS[step];
  const Icon = s.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200"
      data-testid="onboarding-overlay"
    >
      <div className="bg-white rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl">
        {/* Close */}
        <button
          onClick={finish}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"
          aria-label="close"
          data-testid="onboarding-skip"
        >
          <X size={22} />
        </button>

        {/* Step icon */}
        <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#5B47E0] grid place-items-center text-white mx-auto">
          <Icon size={30} weight="fill" />
        </div>

        {/* Title + body */}
        <h3
          className="mt-5 font-display text-xl font-bold text-center"
          data-testid={`onboarding-title-${step}`}
        >
          {lang === "tr" ? s.title_tr : s.title_en}
        </h3>
        <p className="mt-2 text-slate-600 text-sm text-center leading-relaxed">
          {lang === "tr" ? s.body_tr : s.body_en}
        </p>

        {/* Progress dots */}
        <div className="mt-6 flex justify-center gap-1.5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? "w-8 bg-[#002FA7]" : "w-1.5 bg-slate-200"
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="mt-6 flex items-center justify-between gap-2">
          <button
            onClick={back}
            disabled={step === 0}
            className="text-sm text-slate-500 hover:text-slate-800 disabled:opacity-30"
            data-testid="onboarding-back"
          >
            {lang === "tr" ? "← Geri" : "← Back"}
          </button>
          <button
            onClick={next}
            className="btn-primary text-sm"
            data-testid="onboarding-next"
          >
            {step < STEPS.length - 1
              ? lang === "tr" ? "Sıradaki →" : "Next →"
              : lang === "tr" ? "Başlayalım" : "Let's go"}
          </button>
        </div>

        <button
          onClick={finish}
          className="mt-4 w-full text-center text-xs text-slate-400 hover:text-slate-600"
          data-testid="onboarding-skip-text"
        >
          {lang === "tr" ? "Turu atla" : "Skip tour"}
        </button>
      </div>
    </div>
  );
}
